import { format, isSameDay, startOfDay, endOfDay, differenceInCalendarDays } from "date-fns";
import { es } from "date-fns/locale";

/** Estados calculados por el backend para cada equipo (GET /material/items). */
export const MATERIAL_ESTADOS = {
  disponible: {
    label: "Disponible",
    dot: "bg-emerald-500",
    badge: "bg-emerald-100 text-emerald-800 border-emerald-200",
    ring: "ring-emerald-500/40",
    kpi: "from-emerald-50 to-emerald-100/60 border-emerald-200 text-emerald-900",
  },
  reservado: {
    label: "Reservado",
    dot: "bg-sky-500",
    badge: "bg-sky-100 text-sky-800 border-sky-200",
    ring: "ring-sky-500/40",
    kpi: "from-sky-50 to-sky-100/60 border-sky-200 text-sky-900",
  },
  en_uso: {
    label: "En uso",
    dot: "bg-amber-500",
    badge: "bg-amber-100 text-amber-800 border-amber-200",
    ring: "ring-amber-500/40",
    kpi: "from-amber-50 to-amber-100/60 border-amber-200 text-amber-900",
  },
  retrasado: {
    label: "Retrasado",
    dot: "bg-red-500",
    badge: "bg-red-100 text-red-800 border-red-200",
    ring: "ring-red-500/50",
    kpi: "from-red-50 to-red-100/60 border-red-200 text-red-900",
  },
  reparacion: {
    label: "En reparación",
    dot: "bg-zinc-500",
    badge: "bg-zinc-200 text-zinc-800 border-zinc-300",
    ring: "ring-zinc-500/40",
    kpi: "from-zinc-50 to-zinc-100/80 border-zinc-300 text-zinc-900",
  },
};

export const MATERIAL_ESTADO_ORDEN = ["disponible", "reservado", "en_uso", "retrasado", "reparacion"];

/** Estados de una reserva concreta. */
export const RESERVA_ESTADOS = {
  reservada: { label: "Reservada", badge: "bg-sky-100 text-sky-800 border-sky-200" },
  en_uso: { label: "En uso", badge: "bg-amber-100 text-amber-800 border-amber-200" },
  devuelta: { label: "Devuelta", badge: "bg-zinc-100 text-zinc-700 border-zinc-200" },
  cancelada: { label: "Cancelada", badge: "bg-zinc-100 text-zinc-500 border-zinc-200 line-through" },
};

export const isReservaRetrasada = (r, now = new Date()) => r?.estado === "en_uso" && new Date(r.fin) < now;

/** "lun 12 oct" o "lun 12 oct, 18:00" según si la reserva es de día completo. */
export function formatMaterialFecha(iso, conHora = true) {
  if (!iso) return "";
  const d = new Date(iso);
  return format(d, conHora ? "EEE d MMM, HH:mm" : "EEE d MMM", { locale: es });
}

/**
 * Rango legible de una reserva. Para día completo, `fin` es el inicio del día siguiente,
 * así que se muestra el último día incluido.
 */
export function formatReservaRango(r) {
  if (!r) return "";
  const ini = new Date(r.inicio);
  const fin = new Date(r.fin);
  if (r.dia_completo && r.estado !== "devuelta") {
    const ultimo = new Date(fin.getTime() - 1);
    if (isSameDay(ini, ultimo)) return format(ini, "EEE d MMM", { locale: es });
    return `${format(ini, "EEE d MMM", { locale: es })} → ${format(ultimo, "EEE d MMM", { locale: es })}`;
  }
  if (isSameDay(ini, fin)) {
    return `${format(ini, "EEE d MMM", { locale: es })}, ${format(ini, "HH:mm")}–${format(fin, "HH:mm")}`;
  }
  return `${format(ini, "EEE d MMM, HH:mm", { locale: es })} → ${format(fin, "EEE d MMM, HH:mm", { locale: es })}`;
}

/** Texto de cuándo vuelve un equipo ("vuelve hoy a las 18:00", "vuelve el jue 15"). */
export function formatVuelve(r, now = new Date()) {
  if (!r) return "";
  const fin = new Date(r.fin);
  if (fin < now) {
    const dias = differenceInCalendarDays(now, fin);
    return dias <= 0 ? `debía volver a las ${format(fin, "HH:mm")}` : `debía volver hace ${dias} día${dias === 1 ? "" : "s"}`;
  }
  const ultimo = r.dia_completo ? new Date(fin.getTime() - 1) : fin;
  if (isSameDay(ultimo, now)) return r.dia_completo ? "vuelve hoy" : `vuelve hoy a las ${format(fin, "HH:mm")}`;
  return r.dia_completo
    ? `vuelve el ${format(ultimo, "EEE d", { locale: es })}`
    : `vuelve el ${format(fin, "EEE d, HH:mm", { locale: es })}`;
}

/** Convierte selección de días (+ horas opcionales) al rango ISO que espera el backend. */
export function buildReservaRango({ desde, hasta, diaCompleto, horaInicio, horaFin }) {
  const dDesde = startOfDay(desde);
  const dHasta = startOfDay(hasta || desde);
  if (diaCompleto) {
    const fin = new Date(endOfDay(dHasta).getTime() + 1);
    return { inicio: dDesde.toISOString(), fin: fin.toISOString() };
  }
  const [hi, mi] = (horaInicio || "09:00").split(":").map(Number);
  const [hf, mf] = (horaFin || "18:00").split(":").map(Number);
  const ini = new Date(dDesde);
  ini.setHours(hi, mi, 0, 0);
  const fin = new Date(dHasta);
  fin.setHours(hf, mf, 0, 0);
  return { inicio: ini.toISOString(), fin: fin.toISOString() };
}

/** Hora "HH:mm" redondeada al próximo cuarto de hora. */
export function horaRedondeada(date = new Date(), sumarHoras = 0) {
  const d = new Date(date.getTime() + sumarHoras * 3600 * 1000);
  d.setMinutes(Math.ceil(d.getMinutes() / 15) * 15, 0, 0);
  return format(d, "HH:mm");
}
